import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, Video, Youtube, Link } from "lucide-react";
import { toast } from "sonner";
import LocationInsights from "@/components/LocationInsights";
import PropertyValuation from "@/components/PropertyValuation";
import PropertyTemplates from "@/components/PropertyTemplates";
import { AmenitiesTab, LegalPricingTab, MediaTab, BasicDetailsTab, SpecificationsTab, MeasurementsTab } from "@/components/propertyForm";
import MediaManagerTab from "@/components/propertyForm/MediaManagerTab";
import {
  validateTextContent,
  validateNumericField,
  handleNumericKeyDown,
  handleNumericPaste,
} from "@/utils/propertyFormValidation";
import {
  ROOM_TYPES,
  PROPERTY_SUBTYPES,
  shouldShowField,
  MAX_IMAGES,
  MAX_VIDEOS,
  MAX_IMAGE_SIZE_MB,
  MAX_VIDEO_SIZE_MB,
} from "@/data/propertyFormConstants";
import { numberToIndianWords, formatIndianShort } from "@/utils/indianNumberFormat";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PropertyForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [activeTab, setActiveTab] = useState('basic');
  const [completedTabs, setCompletedTabs] = useState(isEdit ? ['basic', 'specifications', 'measurements', 'amenities', 'legal', 'media', 'insights'] : []);
  const [showTemplates, setShowTemplates] = useState(false);
  const [invalidFields, setInvalidFields] = useState([]); // Track invalid fields for highlighting
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    property_type: "residential",
    property_subtype: "",
    purpose: "sale",
    price: "",
    rate_amount: "",
    rate: "",
    agreement_value: "",
    all_inclusive_price: "",
    // New pricing breakdown fields
    rate_per_sqft: "",
    maintenance_per_sqft: "",
    corpus_fund: "",
    infrastructure_cost: "",
    stamp_duty_percent: "7",
    gst_percent: "",
    total_price_per_sqft: "",
    state: "",
    city: "",
    location: "",
    area: "",
    built_up_area: "",
    super_built_up_area: "",
    bedrooms: "",
    bathrooms: "",
    balconies: "",
    furnishing: "",
    furnishing_details: "",
    parking: "",
    parking_type: "",
    floor_number: "",
    total_floors: "",
    tower: "",
    wing: "",
    facing: "",
    overlooking: "",
    age_of_property: "",
    possession_status: "",
    possession_date: "",
    flooring_type: "",
    water_supply: "",
    power_backup: "",
    lift_available: false,
    number_of_lifts: "",
    lift_type: "",
    escalator_available: false,
    security: "",
    amenities: [],
    property_features: [],
    transaction_type: "",
    ownership_type: "",
    approved_by: "",
    rera_registered: false,
    rera_id: "",
    maintenance_charges: "",
    legal_charges: "",
    booking_amount: "",
    existing_loan_amount: "",
    financial_institute_name: "",
    room_measurements: [],
    office_room_measurements: [],  // For office properties
    plot_dimensions: { length: "", width: "", area: "", unit: "sq_ft" },
    // Commercial/Office specific
    total_cabins: "",
    workstations: "",
    washrooms: "",
    frontage_width: "",
    ceiling_height: "",
    display_windows: "",
    loading_docks: "",
    floor_strength: "",
    crane_facility: false,
    total_seats: "",
    meeting_rooms: "",
    conference_rooms: "",
    server_room: false,
    total_rooms: "",
    banquet_halls: "",
    restaurant_area: "",
    seating_capacity: "",
    kitchen_area: "",
    outdoor_seating: false,
    total_beds: "", // For co-living spaces
    // Commercial Building specific size fields
    floor_plate_area: "", // Area of floor plate
    total_carpet_area: "", // Total carpet area in sqft
    total_saleable_area: "", // Total saleable area in sqft
    // Land specific
    plot_length: "",
    plot_width: "",
    road_width: "",
    corner_plot: false,
    boundary_wall: false,
    gated_community: false,
    fsi_available: "",
    // Zone fields
    residential_zone: false,
    commercial_zone: false,
    industrial_zone: false,
    agricultural_zone: false,
    mixed_use_zone: false,
    pollution_clearance: false,
    soil_type: "",
    water_source: "",
    irrigation_facility: "",
    road_access: "",
    electricity_available: false,
    farm_house_allowed: false,
    existing_crops: "",
    bore_well: false,
    na_order_available: false,
    land_use_permission: "",
    youtube_video_url: "",  // YouTube video link
    status: "available",
    // Rental/Lease specific fields
    monthly_rent: "",
    rent_type: "lumpsum", // lumpsum or per_sqft
    rent_per_sqft_carpet: "",
    rent_per_sqft_builtup: "",
    rent_sqft_type: "carpet", // carpet or saleable/builtup
    security_deposit: "",
    security_deposit_type: "lumpsum", // lumpsum or months
    security_deposit_months: "",
    lock_in_period: "",
    lease_duration: "",
    rent_escalation: "",
    maintenance_included: false,
    electricity_included: false,
    water_included: false,
    rent_negotiable: false,
    preferred_tenant: "",
    available_from: "",
    developer_id: null,
    mandate_id: null,
    project_id: null,
  });
  // Plan image files (Sizes tab uploads)
  const [planFiles, setPlanFiles] = useState({
    floor_plan_image: null,
    project_plan_image: null,
    location_image: null,
    room_floor_plan_image: null,
    three_d_image: null,
  });
  const [existingPlanUrls, setExistingPlanUrls] = useState({
    floor_plan_image: null,
    project_plan_image: null,
    location_image: null,
    room_floor_plan_image: null,
    three_d_image: null,
  });

  // Multi-file media state (Emergent Object Storage)
  const [mediaState, setMediaState] = useState({
    floor_plan_files: [],
    project_plan_files: [],
    location_map_files: [],
    room_floor_plan_files: [],
    three_d_view_files: [],
    image_files: [],
    video_files: [],
    external_links: [],
  });
  const [pendingMediaFiles, setPendingMediaFiles] = useState({
    floor_plan: [], project_plan: [], location_map: [],
    room_floor_plan: [], three_d_view: [], images: [], videos: [],
  });

  const [files, setFiles] = useState([]);
  const [existingMedia, setExistingMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEdit);
  
  // Sourcing Manager developer/mandate state
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const currentUserRole = currentUser.role;
  const [smDevelopers, setSmDevelopers] = useState([]);
  const [smMandates, setSmMandates] = useState([]);
  const [smProjects, setSmProjects] = useState([]);
  const [showAddDeveloperModal, setShowAddDeveloperModal] = useState(false);
  const [showAddMandateModal, setShowAddMandateModal] = useState(false);
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [newDevForm, setNewDevForm] = useState({ name: "", contact: "", address: "", details: "", google_location: "" });
  const [newMandateForm, setNewMandateForm] = useState({ name: "", contact: "", address: "", details: "" });
  const [newProjectForm, setNewProjectForm] = useState({ name: "", project_type: "", address: "", google_location: "", details: "" });

  // Apply template to form
  const applyTemplate = (template) => {
    setFormData(prev => ({
      ...prev,
      property_type: template.property_type || prev.property_type,
      property_subtype: template.property_subtype || prev.property_subtype,
      bedrooms: template.bedrooms || prev.bedrooms,
      bathrooms: template.bathrooms || prev.bathrooms,
      balconies: template.balconies || prev.balconies,
      furnishing: template.furnishing || prev.furnishing,
      area: template.area || prev.area,
      carpet_area: template.carpet_area || prev.carpet_area,
      super_built_up_area: template.super_built_up_area || prev.super_built_up_area,
      floor_number: template.floor || prev.floor_number,
      total_floors: template.total_floors || prev.total_floors,
      facing: template.facing || prev.facing,
      parking: template.parking || prev.parking,
      amenities: template.amenities || prev.amenities,
      features: template.features || prev.features,
      plot_dimensions: template.plot_dimensions || prev.plot_dimensions,
      corner_plot: template.corner_plot ?? prev.corner_plot,
      na_order_available: template.na_order_available ?? prev.na_order_available,
      boundary_wall: template.boundary_wall ?? prev.boundary_wall,
      bore_well: template.bore_well ?? prev.bore_well,
    }));
    setShowTemplates(false);
  };

  useEffect(() => {
    if (isEdit) {
      fetchProperty();
    }
  }, [id]);

  // Fetch developers/mandates for sourcing managers
  useEffect(() => {
    if (currentUserRole !== 'sourcing_manager') return;
    const fetchSmData = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const [devRes, mandRes, projRes, ctxRes] = await Promise.all([
          axios.get(`${API}/developers`, { headers }),
          axios.get(`${API}/mandates`, { headers }),
          axios.get(`${API}/projects`, { headers }),
          axios.get(`${API}/users/my-context`, { headers })
        ]);
        setSmDevelopers(devRes.data);
        setSmMandates(mandRes.data);
        setSmProjects(projRes.data);
        // Pre-fill from active context
        if (ctxRes.data.active_developer_id) {
          setFormData(prev => ({ ...prev, developer_id: ctxRes.data.active_developer_id }));
        }
        if (ctxRes.data.active_mandate_id) {
          setFormData(prev => ({ ...prev, mandate_id: ctxRes.data.active_mandate_id }));
        }
        if (ctxRes.data.active_project_id) {
          setFormData(prev => ({ ...prev, project_id: ctxRes.data.active_project_id }));
        }
      } catch (e) { /* silent */ }
    };
    fetchSmData();
  }, [currentUserRole]);

  const fetchProperty = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/properties/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const property = response.data;
      setFormData({
        title: property.title,
        description: property.description,
        property_type: property.property_type,
        property_subtype: property.property_subtype || "",
        purpose: property.purpose,
        price: property.price,
        rate_amount: property.rate_amount || "",
        rate: property.rate || "",
        agreement_value: property.agreement_value || "",
        all_inclusive_price: property.all_inclusive_price || "",
        // New pricing fields
        rate_per_sqft: property.rate_per_sqft || "",
        maintenance_per_sqft: property.maintenance_per_sqft || "",
        corpus_fund: property.corpus_fund || "",
        infrastructure_cost: property.infrastructure_cost || "",
        stamp_duty_percent: property.stamp_duty_percent || "7",
        gst_percent: property.gst_percent || "",
        total_price_per_sqft: property.total_price_per_sqft || "",
        location: property.location,
        area: property.area,
        built_up_area: property.built_up_area || "",
        super_built_up_area: property.super_built_up_area || "",
        bedrooms: property.bedrooms || "",
        bathrooms: property.bathrooms || "",
        balconies: property.balconies || "",
        furnishing: property.furnishing || "",
        furnishing_details: property.furnishing_details || "",
        parking: property.parking || "",
        parking_type: property.parking_type || "",
        floor_number: property.floor_number || "",
        total_floors: property.total_floors || "",
        tower: property.tower || "",
        wing: property.wing || "",
        facing: property.facing || "",
        overlooking: property.overlooking || "",
        age_of_property: property.age_of_property || "",
        possession_status: property.possession_status || "",
        possession_date: property.possession_date || "",
        flooring_type: property.flooring_type || "",
        water_supply: property.water_supply || "",
        power_backup: property.power_backup || "",
        lift_available: property.lift_available || false,
        number_of_lifts: property.number_of_lifts || "",
        lift_type: property.lift_type || "",
        escalator_available: property.escalator_available || false,
        security: property.security || "",
        amenities: property.amenities || [],
        property_features: property.property_features || [],
        transaction_type: property.transaction_type || "",
        ownership_type: property.ownership_type || "",
        approved_by: property.approved_by || "",
        rera_registered: property.rera_registered || false,
        rera_id: property.rera_id || "",
        maintenance_charges: property.maintenance_charges || "",
        legal_charges: property.legal_charges || "",
        booking_amount: property.booking_amount || "",
        existing_loan_amount: property.existing_loan_amount || "",
        financial_institute_name: property.financial_institute_name || "",
        room_measurements: property.room_measurements || [],
        office_room_measurements: property.office_room_measurements || [],
        plot_dimensions: property.plot_dimensions || { length: "", width: "", area: "", unit: "sq_ft" },
        // Commercial/Office specific
        total_cabins: property.total_cabins || "",
        workstations: property.workstations || "",
        washrooms: property.washrooms || "",
        frontage_width: property.frontage_width || "",
        ceiling_height: property.ceiling_height || "",
        display_windows: property.display_windows || "",
        loading_docks: property.loading_docks || "",
        floor_strength: property.floor_strength || "",
        crane_facility: property.crane_facility || false,
        total_seats: property.total_seats || "",
        meeting_rooms: property.meeting_rooms || "",
        conference_rooms: property.conference_rooms || "",
        server_room: property.server_room || false,
        total_rooms: property.total_rooms || "",
        banquet_halls: property.banquet_halls || "",
        restaurant_area: property.restaurant_area || "",
        seating_capacity: property.seating_capacity || "",
        kitchen_area: property.kitchen_area || "",
        outdoor_seating: property.outdoor_seating || false,
        total_beds: property.total_beds || "",
        // Commercial Building size fields
        floor_plate_area: property.floor_plate_area || "",
        total_carpet_area: property.total_carpet_area || "",
        total_saleable_area: property.total_saleable_area || "",
        // Land specific
        plot_length: property.plot_length || "",
        plot_width: property.plot_width || "",
        road_width: property.road_width || "",
        corner_plot: property.corner_plot || false,
        boundary_wall: property.boundary_wall || false,
        gated_community: property.gated_community || false,
        fsi_available: property.fsi_available || "",
        residential_zone: property.residential_zone || false,
        commercial_zone: property.commercial_zone || false,
        industrial_zone: property.industrial_zone || false,
        agricultural_zone: property.agricultural_zone || false,
        mixed_use_zone: property.mixed_use_zone || false,
        pollution_clearance: property.pollution_clearance || false,
        soil_type: property.soil_type || "",
        water_source: property.water_source || "",
        irrigation_facility: property.irrigation_facility || "",
        road_access: property.road_access || "",
        electricity_available: property.electricity_available || false,
        farm_house_allowed: property.farm_house_allowed || false,
        existing_crops: property.existing_crops || "",
        bore_well: property.bore_well || false,
        na_order_available: property.na_order_available || false,
        land_use_permission: property.land_use_permission || "",
        youtube_video_url: property.youtube_video_url || "",
        status: property.status,
        // Rental/Lease specific fields
        monthly_rent: property.monthly_rent || "",
        rent_type: property.rent_type || "lumpsum",
        rent_per_sqft_carpet: property.rent_per_sqft_carpet || "",
        rent_per_sqft_builtup: property.rent_per_sqft_builtup || "",
        rent_sqft_type: property.rent_sqft_type || "carpet",
        security_deposit: property.security_deposit || "",
        security_deposit_type: property.security_deposit_type || "lumpsum",
        security_deposit_months: property.security_deposit_months || "",
        lock_in_period: property.lock_in_period || "",
        lease_duration: property.lease_duration || "",
        rent_escalation: property.rent_escalation || "",
        maintenance_included: property.maintenance_included || false,
        electricity_included: property.electricity_included || false,
        water_included: property.water_included || false,
        rent_negotiable: property.rent_negotiable || false,
        preferred_tenant: property.preferred_tenant || "",
        available_from: property.available_from || "",
      });
      // Separate images and videos from media_files
      const allMedia = property.media_files || [];
      const videoExtensions = ['.mp4', '.mov', '.webm', '.avi', '.mkv'];
      const videoUrl = allMedia.find(url => videoExtensions.some(ext => url.toLowerCase().includes(ext)));
      const imageUrls = allMedia.filter(url => !videoExtensions.some(ext => url.toLowerCase().includes(ext)));
      
      setExistingMedia(imageUrls);
      setExistingVideo(videoUrl || null);
      // Load existing plan images
      setExistingPlanUrls({
        floor_plan_image: property.floor_plan_image || null,
        project_plan_image: property.project_plan_image || null,
        location_image: property.location_image || null,
        room_floor_plan_image: property.room_floor_plan_image || null,
        three_d_image: property.three_d_image || null,
      });
      // Load multi-file media state from Emergent storage
      setMediaState({
        floor_plan_files: property.floor_plan_files || [],
        project_plan_files: property.project_plan_files || [],
        location_map_files: property.location_map_files || [],
        room_floor_plan_files: property.room_floor_plan_files || [],
        three_d_view_files: property.three_d_view_files || [],
        image_files: property.image_files || [],
        video_files: property.video_files || [],
        external_links: property.external_links || [],
      });
    } catch (error) {
      toast.error("Failed to load property");
      navigate("/properties");
    } finally {
      setInitialLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  // Auto-fill area from Property Size Details when available (only for full building types)
  useEffect(() => {
    const fullBuildingTypes = ['commercial_building', 'mall', 'warehouse', 'industrial_shed', 'it_park', 'hotel', 'lodge', 
      'co_living', 'co_working', 'business_center', 'hospital', 'school', 'college', 'banquet_hall',
      'independent_house', 'villa', 'bungalow', 'bungalow_scheme', 'twin_bungalow', 'row_house', 'farmhouse', 'penthouse'];
    const isFullBuildingType = fullBuildingTypes.includes(formData.property_subtype);
    
    if (isFullBuildingType && !formData.area && (formData.total_carpet_area || formData.floor_plate_area)) {
      const autoFillArea = formData.total_carpet_area || formData.floor_plate_area;
      setFormData(prev => ({ ...prev, area: autoFillArea }));
    }
  }, [formData.total_carpet_area, formData.floor_plate_area, formData.area, formData.property_subtype]);

  const handleAmenityToggle = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const handleFeatureToggle = (feature) => {
    setFormData(prev => ({
      ...prev,
      property_features: prev.property_features.includes(feature)
        ? prev.property_features.filter(f => f !== feature)
        : [...prev.property_features, feature]
    }));
  };

  // Room Measurements handlers
  const addRoomMeasurement = () => {
    setFormData(prev => ({
      ...prev,
      room_measurements: [
        ...prev.room_measurements,
        { room_type: "Bedroom", length: "", width: "", area: "", unit: "sq_ft" }
      ]
    }));
  };

  const updateRoomMeasurement = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.room_measurements];
      updated[index] = { ...updated[index], [field]: value };
      // Auto-calculate area if length and width are provided
      if ((field === 'length' || field === 'width') && updated[index].length && updated[index].width) {
        updated[index].area = (parseFloat(updated[index].length) * parseFloat(updated[index].width)).toFixed(2);
      }
      return { ...prev, room_measurements: updated };
    });
  };

  const removeRoomMeasurement = (index) => {
    setFormData(prev => ({
      ...prev,
      room_measurements: prev.room_measurements.filter((_, i) => i !== index)
    }));
  };

  // Office Room Measurements handlers
  const addOfficeRoomMeasurement = () => {
    setFormData(prev => ({
      ...prev,
      office_room_measurements: [
        ...prev.office_room_measurements,
        { room_type: "Reception Area", length: "", width: "", area: "", unit: "sq_ft" }
      ]
    }));
  };

  const updateOfficeRoomMeasurement = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.office_room_measurements];
      updated[index] = { ...updated[index], [field]: value };
      // Auto-calculate area if length and width are provided
      if ((field === 'length' || field === 'width') && updated[index].length && updated[index].width) {
        updated[index].area = (parseFloat(updated[index].length) * parseFloat(updated[index].width)).toFixed(2);
      }
      return { ...prev, office_room_measurements: updated };
    });
  };

  const removeOfficeRoomMeasurement = (index) => {
    setFormData(prev => ({
      ...prev,
      office_room_measurements: prev.office_room_measurements.filter((_, i) => i !== index)
    }));
  };

  const updatePlotDimensions = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev.plot_dimensions, [field]: value };
      // Auto-calculate area if length and width are provided
      if ((field === 'length' || field === 'width') && updated.length && updated.width) {
        updated.area = (parseFloat(updated.length) * parseFloat(updated.width)).toFixed(2);
      }
      return { ...prev, plot_dimensions: updated };
    });
  };

  // Prevent non-numeric input in number fields
  const handleNumericKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter, decimal point, minus
    if (
      [8, 9, 13, 27, 46, 110, 190].includes(e.keyCode) || // Special keys
      (e.keyCode === 189 || e.keyCode === 109) || // Minus sign
      (e.keyCode >= 35 && e.keyCode <= 40) || // Home, End, arrows
      (e.ctrlKey && [65, 67, 86, 88].includes(e.keyCode)) // Ctrl+A, C, V, X
    ) {
      return;
    }
    // Allow: numbers (0-9) from main keyboard and numpad
    if (
      (e.keyCode >= 48 && e.keyCode <= 57) || // 0-9 main keyboard
      (e.keyCode >= 96 && e.keyCode <= 105) // 0-9 numpad
    ) {
      return;
    }
    // Block everything else
    e.preventDefault();
  };

  // Handle numeric input paste - strip non-numeric characters
  const handleNumericPaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const numericOnly = pastedText.replace(/[^0-9.-]/g, '');
    const input = e.target;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const currentValue = input.value;
    const newValue = currentValue.substring(0, start) + numericOnly + currentValue.substring(end);
    
    // Update the input value
    input.value = newValue;
    
    // Trigger change event
    const changeEvent = new Event('input', { bubbles: true });
    input.dispatchEvent(changeEvent);
  };

  // Real-time text content validation
  const [textWarnings, setTextWarnings] = useState({});
  
  const handleTextChange = (e) => {
    const { name, value } = e.target;
    handleChange(e);
    
    // Validate text content in real-time
    if (value && value.length > 3) {
      const validation = validateTextContent(value, name);
      if (!validation.isValid) {
        setTextWarnings(prev => ({ ...prev, [name]: validation.error }));
      } else {
        setTextWarnings(prev => {
          const updated = { ...prev };
          delete updated[name];
          return updated;
        });
      }
    } else {
      setTextWarnings(prev => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  // Image and Video upload constants
  const MAX_IMAGES = 4;
  const MAX_VIDEOS = 1;
  const MAX_IMAGE_SIZE_MB = 5; // 5 MB per image
  const MAX_VIDEO_SIZE_MB = 10; // 10 MB for video
  const VIDEO_MIN_DURATION = 8;
  const VIDEO_MAX_DURATION = 10;
  
  const CAPIN_LOGO_URL = "https://customer-assets.emergentagent.com/job_realtor-dash-3/artifacts/r98q2q3d_Capin%20Realty%20Logo.jpg";

  const [imageFiles, setImageFiles] = useState([]);
  const [videoFile, setVideoFile] = useState(null);
  const [existingVideo, setExistingVideo] = useState(null);
  const [deletingMedia, setDeletingMedia] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Get current user for delete permissions
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const canDeleteMedia = user.role === 'admin' || (isEdit && existingMedia.length > 0);

  // Calculate available slots based on existing media
  const existingImageCount = existingMedia.filter(url => !url.includes('video')).length;
  const availableImageSlots = Math.max(0, MAX_IMAGES - existingImageCount);
  const totalImageCount = existingImageCount + imageFiles.length;
  const hasExistingVideo = !!existingVideo;
  const hasYoutubeLink = !!formData.youtube_video_url;

  // Delete existing media file
  const handleDeleteExistingMedia = async (mediaUrl, index) => {
    if (deletingMedia !== null) return; // Prevent multiple deletions
    
    setDeletingMedia(index);
    try {
      const token = localStorage.getItem("token");
      // Update property to remove the media file using PATCH endpoint
      const updatedMedia = existingMedia.filter((_, i) => i !== index);
      
      await axios.patch(
        `${API}/properties/${id}/media`,
        { media_files: updatedMedia },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setExistingMedia(updatedMedia);
      toast.success("Media deleted successfully");
    } catch (error) {
      console.error("Delete media error:", error);
      toast.error("Failed to delete media");
    } finally {
      setDeletingMedia(null);
    }
  };

  // Delete existing video
  const handleDeleteExistingVideo = async () => {
    if (!existingVideo) return;
    
    try {
      const token = localStorage.getItem("token");
      // Update property to remove the video URL from media files using PATCH endpoint
      const updatedMedia = existingMedia.filter(url => url !== existingVideo);
      
      await axios.patch(
        `${API}/properties/${id}/media`,
        { media_files: updatedMedia },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setExistingMedia(updatedMedia);
      setExistingVideo(null);
      toast.success("Video deleted successfully");
    } catch (error) {
      console.error("Delete video error:", error);
      toast.error("Failed to delete video");
    }
  };

  // Clear YouTube URL
  const handleClearYoutubeUrl = async () => {
    if (isEdit) {
      try {
        const token = localStorage.getItem("token");
        await axios.patch(
          `${API}/properties/${id}/media`,
          { youtube_video_url: "" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setFormData({ ...formData, youtube_video_url: "" });
        toast.success("YouTube link removed successfully");
      } catch (error) {
        console.error("Clear YouTube error:", error);
        toast.error("Failed to remove YouTube link");
      }
    } else {
      setFormData({ ...formData, youtube_video_url: "" });
    }
  };

  // Validate image file size (max 5MB)
  const validateImageSize = (file) => {
    const sizeInMB = file.size / (1024 * 1024);
    return sizeInMB <= MAX_IMAGE_SIZE_MB;
  };

  // Validate video file size (max 10MB) and duration (8-10 seconds)
  const validateVideoFile = (file) => {
    return new Promise((resolve) => {
      const sizeInMB = file.size / (1024 * 1024);
      if (sizeInMB > MAX_VIDEO_SIZE_MB) {
        resolve({ isValid: false, error: `Video size must be under ${MAX_VIDEO_SIZE_MB}MB. Your file is ${sizeInMB.toFixed(1)}MB.` });
        return;
      }
      
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        const duration = video.duration;
        if (duration < VIDEO_MIN_DURATION || duration > VIDEO_MAX_DURATION) {
          resolve({ isValid: false, error: `Video must be ${VIDEO_MIN_DURATION}-${VIDEO_MAX_DURATION} seconds. Your video is ${duration.toFixed(1)} seconds.` });
        } else {
          resolve({ isValid: true, duration, size: sizeInMB });
        }
      };
      video.onerror = () => resolve({ isValid: false, error: 'Could not read video file.' });
      video.src = URL.createObjectURL(file);
    });
  };

  // Content moderation - check if image is appropriate for real estate
  const moderateImageContent = async (file) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return { isAppropriate: true }; // Skip for guests
      
      // Convert file to base64
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result.split(',')[1];
          resolve(base64String);
        };
        reader.readAsDataURL(file);
      });
      
      const response = await axios.post(
        `${API}/media/moderate`,
        { image_base64: base64, filename: file.name },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      return {
        isAppropriate: response.data.is_appropriate,
        reason: response.data.reason,
        category: response.data.category
      };
    } catch (error) {
      console.error("Content moderation error:", error);
      return { isAppropriate: true }; // Allow on error
    }
  };

  const handleImageChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    // Check max images limit including existing media
    const currentTotalImages = existingMedia.length + imageFiles.length;
    if (currentTotalImages + selectedFiles.length > MAX_IMAGES) {
      const available = MAX_IMAGES - currentTotalImages;
      toast.error(`Maximum ${MAX_IMAGES} images allowed. You can add ${available > 0 ? available : 0} more.`);
      return;
    }

    const validImages = [];
    for (const file of selectedFiles) {
      // Check file size
      if (!validateImageSize(file)) {
        const sizeInMB = (file.size / (1024 * 1024)).toFixed(1);
        toast.error(`"${file.name}" exceeds ${MAX_IMAGE_SIZE_MB}MB limit (${sizeInMB}MB). Please compress the image.`);
        continue;
      }
      
      // Content moderation check
      toast.loading(`Checking "${file.name}" for appropriate content...`, { id: `moderate-${file.name}` });
      const moderation = await moderateImageContent(file);
      toast.dismiss(`moderate-${file.name}`);
      
      if (!moderation.isAppropriate) {
        toast.error(
          `"${file.name}" rejected: ${moderation.reason}. Only real estate related images are allowed.`,
          { duration: 5000 }
        );
        continue;
      }
      
      validImages.push(file);
    }

    if (validImages.length > 0) {
      setImageFiles(prev => [...prev, ...validImages]);
      toast.success(`${validImages.length} image(s) added successfully`);
    }
  };

  // Extract first frame from video for moderation
  const extractVideoFrame = (file) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      
      video.onloadedmetadata = () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
      };
      
      video.onseeked = () => {
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);
        const base64 = canvas.toDataURL('image/jpeg', 0.5).split(',')[1];
        URL.revokeObjectURL(video.src);
        resolve(base64);
      };
      
      video.onerror = () => {
        resolve(null);
      };
      
      video.src = URL.createObjectURL(file);
      video.currentTime = 1; // Get frame at 1 second
    });
  };

  const handleVideoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check if video already exists (uploaded file or existing)
    if (videoFile || existingVideo) {
      toast.error('Only 1 video allowed per property. Remove existing video first.');
      return;
    }

    // Validate size and duration
    const result = await validateVideoFile(file);
    if (!result.isValid) {
      toast.error(result.error);
      return;
    }
    
    // Content moderation - extract frame and check
    toast.loading('Checking video content...', { id: 'video-moderate' });
    const frameBase64 = await extractVideoFrame(file);
    
    if (frameBase64) {
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const response = await axios.post(
            `${API}/media/moderate`,
            { image_base64: frameBase64, filename: file.name },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          toast.dismiss('video-moderate');
          
          if (!response.data.is_appropriate) {
            toast.error(
              `Video rejected: ${response.data.reason}. Only real estate related videos are allowed.`,
              { duration: 5000 }
            );
            return;
          }
        }
      } catch (error) {
        console.error("Video moderation error:", error);
      }
    }
    toast.dismiss('video-moderate');
    
    setVideoFile(file);
    toast.success(`Video added successfully (${result.duration.toFixed(1)} seconds, ${result.size.toFixed(1)}MB)`);
  };

  const removeImageFile = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeVideoFile = () => {
    setVideoFile(null);
  };

  // Plan image upload handler (Floor Plan, Project Plan, Location, Room Floor Plan, 3D Image)
  const handlePlanImageChange = async (e, planType) => {
    const file = e.target.files[0];
    if (!file) return;
    // Validate file size (max 5MB)
    if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
      toast.error(`Image exceeds ${MAX_IMAGE_SIZE_MB}MB limit. Please compress it.`);
      return;
    }
    // Content moderation
    toast.loading(`Checking image...`, { id: `moderate-plan-${planType}` });
    const moderation = await moderateImageContent(file);
    toast.dismiss(`moderate-plan-${planType}`);
    if (!moderation.isAppropriate) {
      toast.error(`Image rejected: ${moderation.reason}. Only real estate related images are allowed.`, { duration: 5000 });
      return;
    }
    setPlanFiles(prev => ({ ...prev, [planType]: file }));
    toast.success('Image added successfully');
  };

  const removePlanFile = (planType) => {
    setPlanFiles(prev => ({ ...prev, [planType]: null }));
  };

  // Multi-file media management handlers (Emergent Object Storage)
  const handleMediaStateUpdate = (fieldName, files) => {
    setMediaState(prev => ({ ...prev, [fieldName]: files }));
  };
  const handlePendingFileAdd = (categoryKey, file) => {
    setPendingMediaFiles(prev => ({ ...prev, [categoryKey]: [...(prev[categoryKey] || []), file] }));
  };
  const handlePendingFileRemove = (categoryKey, index) => {
    setPendingMediaFiles(prev => ({ ...prev, [categoryKey]: (prev[categoryKey] || []).filter((_, i) => i !== index) }));
  };
  const handleLinkAdded = (link) => {
    setMediaState(prev => ({ ...prev, external_links: [...(prev.external_links || []), link] }));
  };
  const handleLinkRemoved = (linkId) => {
    setMediaState(prev => ({ ...prev, external_links: (prev.external_links || []).filter(l => l.id !== linkId) }));
  };

  // Legacy file handling for backward compatibility
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    // Check if sourcing manager has active context set
    const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
    if (currentUser.role === 'sourcing_manager' && !isEdit) {
      try {
        const token = localStorage.getItem("token");
        const ctxRes = await axios.get(`${API}/users/my-context`, { headers: { Authorization: `Bearer ${token}` } });
        if (!ctxRes.data.active_developer_id && !ctxRes.data.active_mandate_id) {
          toast.error("Please select a Developer or Mandate from the context switcher in the header before adding a property.");
          return;
        }
      } catch (err) { /* continue */ }
    }
    
    // Determine if this is a rent/lease property
    const isRentOrLease = ['rent', 'lease'].includes(formData.purpose);
    
    // Define full building types that have Property Size Details section
    const fullBuildingTypes = ['commercial_building', 'mall', 'warehouse', 'industrial_shed', 'it_park', 'hotel', 'lodge', 
      'co_living', 'co_working', 'business_center', 'hospital', 'school', 'college', 'banquet_hall',
      'independent_house', 'villa', 'bungalow', 'bungalow_scheme', 'twin_bungalow', 'row_house', 'farmhouse', 'penthouse', 'builder_floor'];
    const isFullBuildingType = fullBuildingTypes.includes(formData.property_subtype);
    
    // Check if area is available from Property Size Details (only for full building types)
    const effectiveArea = formData.area || (isFullBuildingType ? (formData.total_carpet_area || formData.floor_plate_area) : null);
    
    // Validate required fields across all tabs - different for sale vs rent
    const requiredFields = [
      { field: 'title', label: 'Property Title', tab: 'basic' },
      { field: 'description', label: 'Description', tab: 'basic' },
      { field: 'location', label: 'Location', tab: 'basic' }
    ];
    
    // Add price field based on purpose
    if (isRentOrLease) {
      // For rent/lease, check based on rent_type
      if (formData.rent_type === 'per_sqft') {
        const hasPerSqftRent = formData.rent_per_sqft_carpet || formData.rent_per_sqft_builtup;
        if (!hasPerSqftRent) {
          toast.error('Please fill required field: Rent per Sq.Ft');
          setActiveTab('basic');
          return;
        }
      } else {
        requiredFields.push({ field: 'monthly_rent', label: 'Monthly Rent', tab: 'basic' });
      }
    } else {
      // For sale, require EITHER all-inclusive price OR agreement value
      if (!formData.all_inclusive_price && !formData.agreement_value) {
        toast.error('Please fill either All-Inclusive Price or Agreement Value');
        setActiveTab('basic');
        return;
      }
    }
    
    const missingFields = requiredFields.filter(({ field }) => !formData[field]);
    
    if (missingFields.length > 0) {
      const firstMissing = missingFields[0];
      toast.error(`Please fill required field: ${firstMissing.label}`);
      setActiveTab(firstMissing.tab);
      return;
    }

    // Validate transaction_type and ownership_type (mandatory for all)
    if (!formData.transaction_type) {
      toast.error('Please fill required field: Transaction Type');
      setActiveTab('legal');
      return;
    }
    if (!formData.ownership_type) {
      toast.error('Please fill required field: Ownership Type');
      setActiveTab('legal');
      return;
    }

    // Tower and wing are now optional for all roles
    
    // Check for area - required field (either direct or from Property Size Details for full building types)
    if (!effectiveArea || effectiveArea === '0' || effectiveArea === 0) {
      if (isFullBuildingType) {
        toast.error('Please fill required field: Carpet Area (or Total Carpet Area in Property Size Details)');
      } else {
        toast.error('Please fill required field: Carpet Area');
      }
      setActiveTab('basic');
      return;
    }

    // === CONTENT MODERATION FOR TEXT FIELDS ===
    const textFieldsToValidate = [
      { field: 'title', label: 'Property Title' },
      { field: 'description', label: 'Description' },
      { field: 'location', label: 'Location' },
      { field: 'locality', label: 'Locality' },
      { field: 'project_name', label: 'Project Name' },
      { field: 'builder_name', label: 'Builder Name' },
      { field: 'furnishing_details', label: 'Furnishing Details' }
    ];

    for (const { field, label } of textFieldsToValidate) {
      if (formData[field]) {
        const textValidation = validateTextContent(formData[field], label);
        if (!textValidation.isValid) {
          toast.error(textValidation.error, { duration: 5000 });
          setActiveTab('basic');
          return;
        }
      }
    }

    // === NUMERIC FIELD VALIDATION ===
    const numericFieldsToValidate = [
      { field: 'area', label: 'Carpet Area', options: { min: 1, max: 100000000, required: true } },
      { field: 'built_up_area', label: 'Built-up Area', options: { min: 1, max: 100000000 } },
      { field: 'super_built_up_area', label: 'Super Built-up Area', options: { min: 1, max: 100000000 } },
      { field: 'all_inclusive_price', label: 'All-Inclusive Price', options: { min: 1, max: 100000000000 } },
      { field: 'agreement_value', label: 'Agreement Value', options: { min: 0, max: 100000000000 } },
      { field: 'monthly_rent', label: 'Monthly Rent', options: { min: 0, max: 100000000 } },
      { field: 'security_deposit', label: 'Security Deposit', options: { min: 0, max: 100000000 } },
      { field: 'maintenance_charges', label: 'Maintenance Charges', options: { min: 0, max: 10000000 } },
      { field: 'corpus_fund', label: 'Corpus Fund', options: { min: 0, max: 100000000 } },
      { field: 'infrastructure_cost', label: 'Infrastructure Cost', options: { min: 0, max: 100000000 } },
      { field: 'booking_amount', label: 'Booking Amount', options: { min: 0, max: 100000000 } },
      { field: 'stamp_duty_percent', label: 'Stamp Duty %', options: { min: 0, max: 100 } },
      { field: 'gst_percent', label: 'GST %', options: { min: 0, max: 100 } },
      { field: 'rent_escalation', label: 'Rent Escalation %', options: { min: 0, max: 100 } },
      { field: 'bedrooms', label: 'Bedrooms', options: { min: 0, max: 50, allowDecimal: false } },
      { field: 'bathrooms', label: 'Bathrooms', options: { min: 0, max: 50, allowDecimal: false } },
      { field: 'balconies', label: 'Balconies', options: { min: 0, max: 20, allowDecimal: false } },
      { field: 'parking', label: 'Parking Spaces', options: { min: 0, max: 100, allowDecimal: false } },
      { field: 'floor_number', label: 'Floor Number', options: { min: -5, max: 200, allowDecimal: false } },
      { field: 'total_floors', label: 'Total Floors', options: { min: 1, max: 200, allowDecimal: false } },
      { field: 'existing_loan_amount', label: 'Existing Loan Amount', options: { min: 0, max: 100000000000 } },
      { field: 'rent_per_sqft_carpet', label: 'Rent per Sqft (Carpet)', options: { min: 0, max: 100000 } },
      { field: 'rent_per_sqft_builtup', label: 'Rent per Sqft (Built-up)', options: { min: 0, max: 100000 } },
      { field: 'number_of_lifts', label: 'Number of Lifts', options: { min: 0, max: 50, allowDecimal: false } },
      { field: 'total_rooms', label: 'Total Rooms', options: { min: 0, max: 1000, allowDecimal: false } },
      { field: 'total_beds', label: 'Total Beds', options: { min: 0, max: 5000, allowDecimal: false } }
    ];

    for (const { field, label, options } of numericFieldsToValidate) {
      if (formData[field] !== '' && formData[field] !== null && formData[field] !== undefined) {
        const numericValidation = validateNumericField(formData[field], label, options);
        if (!numericValidation.isValid) {
          toast.error(numericValidation.error, { duration: 5000 });
          setActiveTab('basic');
          return;
        }
      }
    }

    // Validate room measurements if any
    if (formData.room_measurements && formData.room_measurements.length > 0) {
      for (let i = 0; i < formData.room_measurements.length; i++) {
        const room = formData.room_measurements[i];
        if (room.length) {
          const lengthValidation = validateNumericField(room.length, `Room ${i + 1} Length`, { min: 0.1, max: 10000 });
          if (!lengthValidation.isValid) {
            toast.error(lengthValidation.error);
            setActiveTab('measurements');
            return;
          }
        }
        if (room.width) {
          const widthValidation = validateNumericField(room.width, `Room ${i + 1} Width`, { min: 0.1, max: 10000 });
          if (!widthValidation.isValid) {
            toast.error(widthValidation.error);
            setActiveTab('measurements');
            return;
          }
        }
      }
    }

    // Validate plot dimensions if any
    if (formData.plot_dimensions) {
      if (formData.plot_dimensions.length) {
        const lengthValidation = validateNumericField(formData.plot_dimensions.length, 'Plot Length', { min: 0.1, max: 100000 });
        if (!lengthValidation.isValid) {
          toast.error(lengthValidation.error);
          setActiveTab('measurements');
          return;
        }
      }
      if (formData.plot_dimensions.width) {
        const widthValidation = validateNumericField(formData.plot_dimensions.width, 'Plot Width', { min: 0.1, max: 100000 });
        if (!widthValidation.isValid) {
          toast.error(widthValidation.error);
          setActiveTab('measurements');
          return;
        }
      }
    }
    
    // All validation passed — show confirmation screen
    setShowConfirmation(true);
    window.scrollTo(0, 0);
  };

  // Actual submission after confirmation
  const handleConfirmedSubmit = async () => {
    const isRentOrLease = ['rent', 'lease'].includes(formData.purpose);
    setLoading(true);
    setShowConfirmation(false);

    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      // Get area value - use formData.area, or fallback to Property Size Details
      const areaValue = formData.area || formData.total_carpet_area || formData.floor_plate_area || '0';
      const area = parseFloat(areaValue) || 1;
      
      // Determine if this is a rent/lease property
      const isRentOrLease = ['rent', 'lease'].includes(formData.purpose);
      
      // Calculate pricing based on purpose
      let priceValue = 0;
      let allInclusivePrice = 0;
      let agreementValue = 0;
      let monthlyRentValue = 0;
      
      if (isRentOrLease) {
        // For rent/lease, use monthly_rent or calculated rent
        if (formData.rent_type === 'per_sqft') {
          const rate = parseFloat(formData.rent_sqft_type === 'carpet' ? formData.rent_per_sqft_carpet : formData.rent_per_sqft_builtup) || 0;
          monthlyRentValue = rate * area;
        } else {
          monthlyRentValue = parseFloat(formData.monthly_rent || 0);
        }
        priceValue = monthlyRentValue; // Use monthly rent as the "price" for lease
      } else {
        // For sale, use all_inclusive_price or agreement_value
        allInclusivePrice = parseFloat(formData.all_inclusive_price || 0);
        agreementValue = parseFloat(formData.agreement_value || 0);
        priceValue = allInclusivePrice || agreementValue || parseFloat(formData.price || 0);
      }
      
      const stampDutyRate = parseFloat(formData.stamp_duty_percent || 7);
      const gstRate = parseFloat(formData.gst_percent || (formData.property_type === 'commercial' ? 12 : 5));
      
      // Calculate rate per sqft from primary price (for sale) or monthly rent (for lease)
      const primaryPrice = allInclusivePrice || agreementValue;
      const ratePerSqft = isRentOrLease ? Math.round(monthlyRentValue / area) : Math.round(primaryPrice / area);
      const baseRatePerSqft = agreementValue ? Math.round(agreementValue / area) : 0;

      const propertyData = {
        ...formData,
        // Include multi-file media fields
        floor_plan_files: mediaState.floor_plan_files || [],
        project_plan_files: mediaState.project_plan_files || [],
        location_map_files: mediaState.location_map_files || [],
        room_floor_plan_files: mediaState.room_floor_plan_files || [],
        three_d_view_files: mediaState.three_d_view_files || [],
        image_files: mediaState.image_files || [],
        video_files: mediaState.video_files || [],
        external_links: mediaState.external_links || [],
        price: priceValue || 1, // Ensure price is never 0
        area: String(areaValue), // Ensure area is a string
        property_subtype: formData.property_subtype || null,
        rate_amount: formData.rate_amount ? parseFloat(formData.rate_amount) : null,
        rate: formData.rate || null,
        // New pricing fields
        all_inclusive_price: allInclusivePrice || null,
        agreement_value: agreementValue || null,
        monthly_rent: monthlyRentValue || parseFloat(formData.monthly_rent) || null,
        rate_per_sqft: ratePerSqft || null,
        maintenance_per_sqft: formData.maintenance_per_sqft ? parseFloat(formData.maintenance_per_sqft) : null,
        corpus_fund: formData.corpus_fund ? parseFloat(formData.corpus_fund) : null,
        infrastructure_cost: formData.infrastructure_cost ? parseFloat(formData.infrastructure_cost) : null,
        stamp_duty_percent: stampDutyRate,
        gst_percent: gstRate,
        total_price_per_sqft: ratePerSqft || null,
        built_up_area: formData.built_up_area || null,
        super_built_up_area: formData.super_built_up_area || null,
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
        bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : null,
        balconies: formData.balconies ? parseInt(formData.balconies) : null,
        furnishing: formData.furnishing || null,
        furnishing_details: formData.furnishing_details || null,
        parking: formData.parking ? parseInt(formData.parking) : null,
        parking_type: formData.parking_type || null,
        floor_number: formData.floor_number ? parseInt(formData.floor_number) : null,
        total_floors: formData.total_floors ? parseInt(formData.total_floors) : null,
        tower: formData.tower || null,
        wing: formData.wing || null,
        facing: formData.facing || null,
        overlooking: formData.overlooking || null,
        age_of_property: formData.age_of_property || null,
        possession_status: formData.possession_status || null,
        possession_date: formData.possession_date || null,
        flooring_type: formData.flooring_type || null,
        water_supply: formData.water_supply || null,
        power_backup: formData.power_backup || null,
        security: formData.security || null,
        transaction_type: formData.transaction_type || null,
        ownership_type: formData.ownership_type || null,
        approved_by: formData.approved_by || null,
        rera_id: formData.rera_id || null,
        maintenance_charges: formData.maintenance_charges ? parseFloat(formData.maintenance_charges) : null,
        legal_charges: formData.legal_charges ? parseFloat(formData.legal_charges) : null,
        booking_amount: formData.booking_amount ? parseFloat(formData.booking_amount) : null,
        room_measurements: formData.room_measurements.length > 0 ? formData.room_measurements : [],
        plot_dimensions: (formData.plot_dimensions.length || formData.plot_dimensions.width || formData.plot_dimensions.area) 
          ? formData.plot_dimensions : null,
        // Commercial Building size fields
        floor_plate_area: formData.floor_plate_area || null,
        total_carpet_area: formData.total_carpet_area || null,
        total_saleable_area: formData.total_saleable_area || null,
        total_beds: formData.total_beds ? parseInt(formData.total_beds) : null,
        // Lift fields
        number_of_lifts: formData.number_of_lifts ? parseInt(formData.number_of_lifts) : null,
        lift_type: formData.lift_type || null,
        // Rental/Lease fields - ensure proper number conversion
        rent_per_sqft_carpet: formData.rent_per_sqft_carpet ? parseFloat(formData.rent_per_sqft_carpet) : null,
        rent_per_sqft_builtup: formData.rent_per_sqft_builtup ? parseFloat(formData.rent_per_sqft_builtup) : null,
        security_deposit: formData.security_deposit ? parseFloat(formData.security_deposit) : null,
        security_deposit_months: formData.security_deposit_months ? parseInt(formData.security_deposit_months) : null,
        rent_escalation: formData.rent_escalation ? parseFloat(formData.rent_escalation) : null,
        lock_in_period: formData.lock_in_period || null,
        lease_duration: formData.lease_duration || null,
      };

      // Clean up propertyData - remove empty strings and convert to null
      Object.keys(propertyData).forEach(key => {
        if (propertyData[key] === '' || propertyData[key] === undefined) {
          propertyData[key] = null;
        }
      });

      let propertyId = id;
      if (isEdit) {
        await axios.put(`${API}/properties/${id}`, propertyData, { headers });
        toast.success("Property updated successfully");
      } else {
        const response = await axios.post(`${API}/properties`, propertyData, { headers });
        propertyId = response.data.id;
        toast.success("Property created successfully");
      }

      // Upload files if any (images and video)
      const allFiles = [...imageFiles];
      if (videoFile) {
        allFiles.push(videoFile);
      }
      
      if (allFiles.length > 0) {
        const formDataFiles = new FormData();
        allFiles.forEach((file) => {
          formDataFiles.append("files", file);
        });
        await axios.post(`${API}/properties/${propertyId}/upload`, formDataFiles, {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
        });
        toast.success("Media uploaded to Cloudinary successfully!");
      }

      // Upload plan images if any
      const planTypes = Object.keys(planFiles);
      for (const planType of planTypes) {
        if (planFiles[planType]) {
          const planFormData = new FormData();
          planFormData.append("file", planFiles[planType]);
          planFormData.append("plan_type", planType);
          await axios.post(`${API}/properties/${propertyId}/upload-plan`, planFormData, {
            headers: { ...headers, "Content-Type": "multipart/form-data" },
          });
        }
      }

      // Upload pending media files to Emergent Object Storage (create mode)
      const categoryKeys = Object.keys(pendingMediaFiles);
      for (const categoryKey of categoryKeys) {
        const catFiles = pendingMediaFiles[categoryKey];
        if (catFiles && catFiles.length > 0) {
          const mediaFormData = new FormData();
          mediaFormData.append("category", categoryKey);
          catFiles.forEach(f => mediaFormData.append("files", f));
          await axios.post(`${API}/properties/${propertyId}/media-upload`, mediaFormData, {
            headers: { ...headers, "Content-Type": "multipart/form-data" },
          });
        }
      }

      // Save external links for new properties
      if (!isEdit && mediaState.external_links?.length > 0) {
        for (const link of mediaState.external_links) {
          await axios.post(`${API}/properties/${propertyId}/external-link`, link, { headers });
        }
      }

      navigate("/properties");
    } catch (error) {
      console.error("Property save error:", error);
      // Handle Pydantic validation errors (which come as an array)
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          // Extract error messages from Pydantic validation errors
          const messages = detail.map(err => {
            const field = err.loc?.join('.') || 'Unknown field';
            return `${field}: ${err.msg}`;
          }).join(', ');
          toast.error(`Validation error: ${messages}`);
        } else if (typeof detail === 'string') {
          toast.error(detail);
        } else {
          toast.error("Failed to save property");
        }
      } else {
        toast.error(error.message || "Failed to save property");
      }
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  const tabs = [
    { id: 'basic', label: 'Basic Details', step: 1 },
    { id: 'specifications', label: 'Specifications', step: 2 },
    { id: 'measurements', label: 'Sizes', step: 3 },
    { id: 'amenities', label: 'Amenities & Features', step: 4 },
    { id: 'legal', label: 'Legal & Pricing', step: 5 },
    { id: 'media', label: 'Media & Plans', step: 6 },
    { id: 'insights', label: 'AI Insights', step: 7 }
  ];

  const currentTabIndex = tabs.findIndex(t => t.id === activeTab);
  const isLastTab = currentTabIndex === tabs.length - 1;
  const isFirstTab = currentTabIndex === 0;

  // Check if a tab is accessible (completed previous tabs or is current)
  const isTabAccessible = (tabId) => {
    if (isEdit) return true; // All tabs accessible in edit mode
    const tabIndex = tabs.findIndex(t => t.id === tabId);
    const currentIndex = tabs.findIndex(t => t.id === activeTab);
    return tabIndex <= currentIndex || completedTabs.includes(tabs[tabIndex - 1]?.id);
  };

  // Validate current tab before proceeding
  const validateCurrentTab = () => {
    // Determine if this is a rent/lease property
    const isRentOrLease = ['rent', 'lease'].includes(formData.purpose);
    
    // Define full building types that have Property Size Details section
    const fullBuildingTypes = ['commercial_building', 'mall', 'warehouse', 'industrial_shed', 'it_park', 'hotel', 'lodge', 
      'co_living', 'co_working', 'business_center', 'hospital', 'school', 'college', 'banquet_hall',
      'independent_house', 'villa', 'bungalow', 'bungalow_scheme', 'twin_bungalow', 'row_house', 'farmhouse', 'penthouse', 'builder_floor'];
    const isFullBuildingType = fullBuildingTypes.includes(formData.property_subtype);
    
    // Check if area is available from Property Size Details (only for full building types)
    const hasAreaFromSizeDetails = isFullBuildingType && (formData.total_carpet_area || formData.floor_plate_area);
    
    // Base validations for basic tab
    const basicValidations = [
      { field: 'title', label: 'Property Title' },
      { field: 'description', label: 'Description' },
      { field: 'property_subtype', label: 'Property Category' },
      { field: 'location', label: 'Location' }
    ];
    
    // Only require carpet area if not provided in Property Size Details (for full building types)
    if (!hasAreaFromSizeDetails) {
      basicValidations.push({ field: 'area', label: 'Carpet Area' });
    }
    
    // Add price field based on purpose
    if (isRentOrLease) {
      // For rent/lease, check based on rent_type
      if (formData.rent_type === 'per_sqft') {
        // Per sq.ft mode - check if rent per sqft is filled
        const rentPerSqftField = formData.rent_sqft_type === 'carpet' ? 'rent_per_sqft_carpet' : 'rent_per_sqft_builtup';
        basicValidations.push({ field: rentPerSqftField, label: 'Rent per Sq.Ft' });
      } else {
        // Lumpsum mode - check monthly_rent
        basicValidations.push({ field: 'monthly_rent', label: 'Monthly Rent' });
      }
    } else {
      // For sale, require EITHER all_inclusive_price OR agreement_value
      if (!formData.all_inclusive_price && !formData.agreement_value) {
        basicValidations.push({ field: 'all_inclusive_price', label: 'All-Inclusive Price or Agreement Value' });
      }
    }
    
    const validations = {
      basic: basicValidations,
      specifications: [],
      legal: []
    };

    const currentValidations = validations[activeTab] || [];
    const newInvalidFields = [];
    
    for (const { field, label } of currentValidations) {
      // Check for area field - might be using standalone id
      const fieldValue = field === 'area' ? (formData.area || formData.total_carpet_area || formData.floor_plate_area) : formData[field];
      if (!fieldValue) {
        newInvalidFields.push(field);
      }
    }
    
    if (newInvalidFields.length > 0) {
      setInvalidFields(newInvalidFields);
      
      // Focus on the first invalid field
      const firstInvalidField = newInvalidFields[0];
      let elementToFocus = document.getElementById(firstInvalidField);
      
      // Try alternative IDs for special fields
      if (!elementToFocus && firstInvalidField === 'area') {
        elementToFocus = document.getElementById('area_standalone');
      }
      
      if (elementToFocus) {
        elementToFocus.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          elementToFocus.focus();
        }, 300);
      }
      
      return false;
    }
    
    // Clear invalid fields if validation passes
    setInvalidFields([]);
    return true;
  };

  // Helper function to check if a field is invalid
  const isFieldInvalid = (fieldName) => {
    return invalidFields.includes(fieldName);
  };

  // Helper function to get field class based on validation state
  const getFieldClassName = (fieldName, baseClass = "") => {
    if (isFieldInvalid(fieldName)) {
      return `${baseClass} border-red-500 border-2 bg-red-50 focus:border-red-500 focus:ring-red-500`;
    }
    return baseClass;
  };

  // Clear invalid state when user starts typing
  const handleChangeWithValidation = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    
    // Clear this field from invalid list if it now has a value
    if (value && invalidFields.includes(name)) {
      setInvalidFields(prev => prev.filter(f => f !== name));
    }
  };

  // Handle Next button click
  const handleNext = () => {
    if (!validateCurrentTab()) return;
    
    // Mark current tab as completed
    if (!completedTabs.includes(activeTab)) {
      setCompletedTabs(prev => [...prev, activeTab]);
    }
    
    // Move to next tab
    const nextTab = tabs[currentTabIndex + 1];
    if (nextTab) {
      setActiveTab(nextTab.id);
    }
  };

  // Handle Previous button click
  const handlePrevious = () => {
    const prevTab = tabs[currentTabIndex - 1];
    if (prevTab) {
      setActiveTab(prevTab.id);
    }
  };

  // Handle tab click (only if accessible)
  const handleTabClick = (tabId) => {
    if (isTabAccessible(tabId)) {
      setActiveTab(tabId);
    } else {
      toast.error('Please complete the previous steps first');
    }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto fade-in">
        <Button
          data-testid="back-button"
          onClick={() => navigate("/properties")}
          variant="ghost"
          className="mb-6 -ml-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Properties
        </Button>

        <div className="bg-white rounded-md border border-slate-200 shadow-sm">
          {showConfirmation ? (
            /* ===== CONFIRMATION/REVIEW SCREEN ===== */
            <div data-testid="property-confirmation-screen">
              <div className="p-6 md:p-8 border-b border-slate-200 bg-gradient-to-r from-[#0f2744] to-[#1e3a5f]">
                <h2 className="text-xl font-bold text-white">Review Property Details</h2>
                <p className="text-sm text-white/70 mt-1">Please review all details before submitting. Click "Edit" to go back and make changes.</p>
              </div>
              <div className="p-4 md:p-8 space-y-6">
                {/* Basic Info */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
                    <h3 className="font-semibold text-[#0f2744] text-sm">Basic Details</h3>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div><span className="text-slate-500">Title:</span> <span className="font-medium ml-1">{formData.title}</span></div>
                    <div><span className="text-slate-500">Location:</span> <span className="font-medium ml-1">{formData.location}</span></div>
                    <div><span className="text-slate-500">Type:</span> <span className="font-medium ml-1 capitalize">{formData.property_type}</span></div>
                    <div><span className="text-slate-500">Category:</span> <span className="font-medium ml-1 capitalize">{formData.property_subtype?.replace(/_/g, ' ')}</span></div>
                    <div><span className="text-slate-500">Purpose:</span> <span className="font-medium ml-1 capitalize">{formData.purpose}</span></div>
                    {formData.description && <div className="md:col-span-2"><span className="text-slate-500">Description:</span> <span className="font-medium ml-1">{formData.description.substring(0, 100)}{formData.description.length > 100 ? '...' : ''}</span></div>}
                  </div>
                </div>

                {/* Pricing */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
                    <h3 className="font-semibold text-[#0f2744] text-sm">Pricing & Area</h3>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    {formData.agreement_value && <div><span className="text-slate-500">Agreement Value:</span> <span className="font-bold text-[#c9a227] ml-1">₹{parseFloat(formData.agreement_value).toLocaleString('en-IN')}</span></div>}
                    {formData.all_inclusive_price && <div><span className="text-slate-500">All-Inclusive Price:</span> <span className="font-bold text-[#c9a227] ml-1">₹{parseFloat(formData.all_inclusive_price).toLocaleString('en-IN')}</span></div>}
                    {formData.monthly_rent && <div><span className="text-slate-500">Monthly Rent:</span> <span className="font-bold text-[#c9a227] ml-1">₹{parseFloat(formData.monthly_rent).toLocaleString('en-IN')}</span></div>}
                    {formData.area && <div><span className="text-slate-500">Carpet Area:</span> <span className="font-medium ml-1">{formData.area} sq ft</span></div>}
                    {formData.built_up_area && <div><span className="text-slate-500">Built-up Area:</span> <span className="font-medium ml-1">{formData.built_up_area} sq ft</span></div>}
                    {formData.super_built_up_area && <div><span className="text-slate-500">Super Built-up:</span> <span className="font-medium ml-1">{formData.super_built_up_area} sq ft</span></div>}
                    {formData.maintenance_per_sqft && <div><span className="text-slate-500">Maintenance/sqft:</span> <span className="font-medium ml-1">₹{formData.maintenance_per_sqft}</span></div>}
                    {formData.legal_charges && <div><span className="text-slate-500">Legal Charges:</span> <span className="font-medium ml-1">₹{parseFloat(formData.legal_charges).toLocaleString('en-IN')}</span></div>}
                  </div>
                </div>

                {/* Specifications */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
                    <h3 className="font-semibold text-[#0f2744] text-sm">Specifications</h3>
                  </div>
                  <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    {formData.bedrooms && <div><span className="text-slate-500">Bedrooms:</span> <span className="font-medium ml-1">{formData.bedrooms}</span></div>}
                    {formData.bathrooms && <div><span className="text-slate-500">Bathrooms:</span> <span className="font-medium ml-1">{formData.bathrooms}</span></div>}
                    {formData.balconies && <div><span className="text-slate-500">Balconies:</span> <span className="font-medium ml-1">{formData.balconies}</span></div>}
                    {formData.parking && <div><span className="text-slate-500">Parking:</span> <span className="font-medium ml-1">{formData.parking}</span></div>}
                    {formData.furnishing && <div><span className="text-slate-500">Furnishing:</span> <span className="font-medium ml-1 capitalize">{formData.furnishing?.replace(/_/g, ' ')}</span></div>}
                    {formData.facing && <div><span className="text-slate-500">Facing:</span> <span className="font-medium ml-1 capitalize">{formData.facing}</span></div>}
                    {formData.total_floors && <div><span className="text-slate-500">Total Floors:</span> <span className="font-medium ml-1">{formData.total_floors}</span></div>}
                    {formData.floor_number && <div><span className="text-slate-500">Floor:</span> <span className="font-medium ml-1">{formData.floor_number}</span></div>}
                    {formData.tower && <div><span className="text-slate-500">Tower:</span> <span className="font-medium ml-1">{formData.tower}</span></div>}
                    {formData.wing && <div><span className="text-slate-500">Wing:</span> <span className="font-medium ml-1">{formData.wing}</span></div>}
                    {formData.age_of_property && <div><span className="text-slate-500">Age:</span> <span className="font-medium ml-1 capitalize">{formData.age_of_property?.replace(/_/g, ' ')}</span></div>}
                    {formData.availability && <div><span className="text-slate-500">Availability:</span> <span className="font-medium ml-1 capitalize">{formData.availability?.replace(/_/g, ' ')}</span></div>}
                  </div>
                </div>

                {/* Media Summary */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
                    <h3 className="font-semibold text-[#0f2744] text-sm">Media & Plans</h3>
                  </div>
                  <div className="p-4 text-sm">
                    <div className="flex flex-wrap gap-4">
                      <span className="text-slate-500">{imageFiles.length + existingMedia.length} image(s)</span>
                      <span className="text-slate-500">{videoFile || existingVideo ? '1 video' : 'No video'}</span>
                      <span className="text-slate-500">{Object.values(planFiles).filter(f => f).length + Object.values(existingPlanUrls).filter(u => u).length} plan(s)</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t border-slate-200">
                  <Button
                    type="button"
                    onClick={() => setShowConfirmation(false)}
                    variant="outline"
                    className="flex-1 h-12 rounded-lg border-[#0f2744] text-[#0f2744] hover:bg-[#0f2744] hover:text-white"
                    data-testid="edit-property-btn"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Edit Details
                  </Button>
                  <Button
                    type="button"
                    onClick={handleConfirmedSubmit}
                    disabled={loading}
                    className="flex-1 h-12 rounded-lg bg-[#c9a227] hover:bg-[#b08d26] text-white font-bold"
                    data-testid="confirm-submit-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                        {isEdit ? 'Updating...' : 'Submitting...'}
                      </span>
                    ) : (
                      <span>{isEdit ? 'Confirm & Update Property' : 'Confirm & Add Property'}</span>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
          <>
          <div className="p-8 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, serif' }}>
                {isEdit ? "Edit Property" : "Add New Property"}
              </h1>
            </div>
          </div>

          {/* Developer, Mandate & Project Name for Sourcing Managers */}
          {currentUserRole === 'sourcing_manager' && !isEdit && (
            <div className="px-4 md:px-8 py-4 border-b border-slate-200 bg-slate-50/50">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="developer_select">Developer</Label>
                  <select
                    id="developer_select"
                    value={formData.developer_id || ""}
                    onChange={(e) => {
                      if (e.target.value === '__add_new__') {
                        setShowAddDeveloperModal(true);
                        return;
                      }
                      setFormData(prev => ({ ...prev, developer_id: e.target.value || null }));
                      const token = localStorage.getItem("token");
                      axios.put(`${API}/users/set-context`, { active_developer_id: e.target.value || null, active_mandate_id: formData.mandate_id || null }, { headers: { Authorization: `Bearer ${token}` } }).then(() => {
                        window.dispatchEvent(new Event('context-changed'));
                      });
                    }}
                    className="mt-1 h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
                    data-testid="developer-select"
                  >
                    <option value="">Select Developer</option>
                    {smDevelopers.map(d => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                    <option value="__add_new__">+ Add New Developer</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="mandate_select">Mandate</Label>
                  <select
                    id="mandate_select"
                    value={formData.mandate_id || ""}
                    onChange={(e) => {
                      if (e.target.value === '__add_new__') {
                        setShowAddMandateModal(true);
                        return;
                      }
                      setFormData(prev => ({ ...prev, mandate_id: e.target.value || null }));
                      const token = localStorage.getItem("token");
                      axios.put(`${API}/users/set-context`, { active_developer_id: formData.developer_id || null, active_mandate_id: e.target.value || null }, { headers: { Authorization: `Bearer ${token}` } }).then(() => {
                        window.dispatchEvent(new Event('context-changed'));
                      });
                    }}
                    className="mt-1 h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
                    data-testid="mandate-select"
                  >
                    <option value="">Select Mandate</option>
                    {smMandates.map(m => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                    <option value="__add_new__">+ Add New Mandate</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="project_select">Project</Label>
                  <select
                    id="project_select"
                    value={formData.project_id || ""}
                    onChange={(e) => {
                      if (e.target.value === '__add_new__') {
                        setShowAddProjectModal(true);
                        return;
                      }
                      setFormData(prev => ({ ...prev, project_id: e.target.value || null }));
                      const token = localStorage.getItem("token");
                      axios.put(`${API}/users/set-context`, { active_project_id: e.target.value || null }, { headers: { Authorization: `Bearer ${token}` } });
                    }}
                    className="mt-1 h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
                    data-testid="project-select"
                  >
                    <option value="">Select Project</option>
                    {smProjects.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                    <option value="__add_new__">+ Add New Project</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Wizard Steps */}
          <div className="border-b border-slate-200 bg-[#f0f4f8]">
            <div className="flex overflow-x-auto px-2 md:px-4 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {tabs.map((tab, index) => {
                const isAccessible = isTabAccessible(tab.id);
                const isActive = activeTab === tab.id;
                const isCompleted = completedTabs.includes(tab.id);
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabClick(tab.id)}
                    disabled={!isAccessible}
                    className={`px-2.5 md:px-4 py-3 md:py-4 text-xs md:text-sm font-medium whitespace-nowrap transition-all flex items-center gap-1.5 md:gap-2 ${
                      isActive
                        ? 'border-b-3 border-[#d4af37] text-[#1e3a5f] bg-white'
                        : isAccessible
                          ? isCompleted 
                            ? 'text-green-700 hover:text-[#1e3a5f]'
                            : 'text-slate-600 hover:text-[#1e3a5f]'
                          : 'text-slate-400 cursor-not-allowed'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <span className={`w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center text-[10px] md:text-xs font-bold ${
                      isActive 
                        ? 'bg-[#1e3a5f] text-white'
                        : isCompleted
                          ? 'bg-green-600 text-white'
                          : isAccessible
                            ? 'bg-slate-300 text-slate-700'
                            : 'bg-slate-200 text-slate-400'
                    }`}>
                      {isCompleted && !isActive ? '✓' : tab.step}
                    </span>
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          <form onSubmit={(e) => e.preventDefault()} className="p-4 md:p-8">
            {/* Basic Details Tab */}
            {activeTab === 'basic' && (
              <BasicDetailsTab
                formData={formData}
                setFormData={setFormData}
                handleChange={handleChange}
                handleTextChange={handleTextChange}
                isFieldInvalid={isFieldInvalid}
                textWarnings={textWarnings}
                invalidFields={invalidFields}
                setInvalidFields={setInvalidFields}
                currentUserRole={currentUserRole}
              />
            )}


            {/* Specifications Tab */}
            {activeTab === 'specifications' && (
              <SpecificationsTab
                formData={formData}
                setFormData={setFormData}
                handleChange={handleChange}
                currentUserRole={currentUserRole}
              />
            )}


            {/* Measurements Tab */}
            {activeTab === 'measurements' && (
              <MeasurementsTab
                formData={formData}
                setFormData={setFormData}
                planFiles={planFiles}
                existingPlanUrls={existingPlanUrls}
                handlePlanImageChange={handlePlanImageChange}
                removePlanFile={removePlanFile}
                addRoomMeasurement={addRoomMeasurement}
                removeRoomMeasurement={removeRoomMeasurement}
                updateRoomMeasurement={updateRoomMeasurement}
                addOfficeRoomMeasurement={addOfficeRoomMeasurement}
                removeOfficeRoomMeasurement={removeOfficeRoomMeasurement}
                updateOfficeRoomMeasurement={updateOfficeRoomMeasurement}
                updatePlotDimensions={updatePlotDimensions}
              />
            )}


            {/* Amenities & Features Tab - Using Extracted Component */}
            {activeTab === 'amenities' && (
              <AmenitiesTab 
                formData={formData}
                onAmenityToggle={handleAmenityToggle}
                onFeatureToggle={handleFeatureToggle}
              />
            )}

            {/* Legal & Pricing Tab - Using Extracted Component */}
            {activeTab === 'legal' && (
              <LegalPricingTab
                formData={formData}
                onInputChange={handleChange}
                onCheckboxChange={(field, checked) => setFormData(prev => ({ ...prev, [field]: checked }))}
              />
            )}

            {/* Media Tab - Enhanced Media Manager with Emergent Object Storage */}
            {activeTab === 'media' && (
              <MediaManagerTab
                formData={formData}
                propertyId={id}
                isEditMode={isEdit}
                mediaState={mediaState}
                pendingMediaFiles={pendingMediaFiles}
                onInputChange={handleChange}
                onMediaStateUpdate={handleMediaStateUpdate}
                onPendingFileAdd={handlePendingFileAdd}
                onPendingFileRemove={handlePendingFileRemove}
                onLinkAdded={handleLinkAdded}
                onLinkRemoved={handleLinkRemoved}
              />
            )}

            {/* AI Insights Tab */}
            {activeTab === 'insights' && (
              <div className="space-y-8">
                {/* Property Valuation Section */}
                <PropertyValuation 
                  location={formData.location}
                  propertyType={formData.property_type}
                  carpetArea={formData.area}
                  builtUpArea={formData.built_up_area}
                  bedrooms={formData.bedrooms}
                  bathrooms={formData.bathrooms}
                  floorNumber={formData.floor_number}
                  totalFloors={formData.total_floors}
                  ageOfProperty={formData.age_of_property}
                  furnishing={formData.furnishing}
                  amenities={formData.amenities}
                />
                
                <hr className="border-slate-200" />
                
                {/* Location Insights Section */}
                <LocationInsights 
                  location={formData.location}
                  propertyType={formData.property_type}
                  purpose={formData.purpose}
                />
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between gap-4 pt-6 border-t border-slate-200 mt-8">
              <div>
                {!isFirstTab && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePrevious}
                    className="rounded-lg border-slate-300"
                    data-testid="prev-button"
                  >
                    ← Previous
                  </Button>
                )}
              </div>
              
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/properties")}
                  className="rounded-lg"
                >
                  Cancel
                </Button>
                
                {isLastTab ? (
                  <Button
                    key="submit-btn"
                    type="button"
                    disabled={loading}
                    onClick={handleSubmit}
                    className="bg-gradient-to-r from-[#d4af37] to-[#b08d26] text-[#1e3a5f] font-semibold hover:shadow-lg rounded-lg px-8"
                    data-testid="submit-button"
                  >
                    {loading ? (
                      <div className="spinner mx-auto" style={{ width: '20px', height: '20px', borderWidth: '2px' }} />
                    ) : isEdit ? (
                      "Save Property"
                    ) : (
                      "Create Property"
                    )}
                  </Button>
                ) : (
                  <Button
                    key="next-btn"
                    type="button"
                    onClick={handleNext}
                    className="bg-gradient-to-r from-[#1e3a5f] to-[#162c46] text-white rounded-lg px-8"
                    data-testid="next-button"
                  >
                    Next →
                  </Button>
                )}
              </div>
            </div>
          </form>
        </>
        )}
        </div>
      </div>

      {/* Property Templates Modal */}
      {showTemplates && (
        <PropertyTemplates
          onSelectTemplate={applyTemplate}
          onClose={() => setShowTemplates(false)}
        />
      )}

      {/* Add Developer Modal */}
      {showAddDeveloperModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowAddDeveloperModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-[90%] max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-[#0f2744] to-[#1e3a5f] p-4 rounded-t-xl">
              <h3 className="text-white font-semibold">Add New Developer</h3>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <Label>Developer Name <span className="text-red-500">*</span></Label>
                <Input value={newDevForm.name} onChange={e => setNewDevForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Green Valley Developers" className="mt-1" data-testid="new-dev-name-input" />
              </div>
              <div>
                <Label>Contact Number <span className="text-red-500">*</span></Label>
                <Input value={newDevForm.contact} onChange={e => setNewDevForm(p => ({ ...p, contact: e.target.value }))} placeholder="+91 XXXXX XXXXX" className="mt-1" data-testid="new-dev-contact-input" />
              </div>
              <div>
                <Label>Address</Label>
                <Input value={newDevForm.address} onChange={e => setNewDevForm(p => ({ ...p, address: e.target.value }))} placeholder="Developer office address" className="mt-1" data-testid="new-dev-address-input" />
              </div>
              <div>
                <Label>Details / Description</Label>
                <textarea value={newDevForm.details} onChange={e => setNewDevForm(p => ({ ...p, details: e.target.value }))} placeholder="Additional details about the developer" className="mt-1 w-full min-h-[80px] rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm focus:border-[#1e3a5f] focus:ring-1 focus:ring-[#1e3a5f] outline-none" data-testid="new-dev-details-input" />
              </div>
              <div>
                <Label>Google Location Link</Label>
                <Input value={newDevForm.google_location} onChange={e => setNewDevForm(p => ({ ...p, google_location: e.target.value }))} placeholder="https://maps.google.com/..." className="mt-1" data-testid="new-dev-location-input" />
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => { setShowAddDeveloperModal(false); setNewDevForm({ name: "", contact: "", address: "", details: "", google_location: "" }); }} className="flex-1">Cancel</Button>
                <Button onClick={async () => {
                  if (!newDevForm.name.trim()) { toast.error("Developer name is required"); return; }
                  if (!newDevForm.contact.trim()) { toast.error("Contact number is required"); return; }
                  try {
                    const token = localStorage.getItem("token");
                    const res = await axios.post(`${API}/developers`, newDevForm, { headers: { Authorization: `Bearer ${token}` } });
                    setSmDevelopers(prev => [res.data, ...prev]);
                    setFormData(prev => ({ ...prev, developer_id: res.data.id }));
                    axios.put(`${API}/users/set-context`, { active_developer_id: res.data.id, active_mandate_id: formData.mandate_id || null }, { headers: { Authorization: `Bearer ${token}` } }).then(() => window.dispatchEvent(new Event('context-changed')));
                    setShowAddDeveloperModal(false);
                    setNewDevForm({ name: "", contact: "", address: "", details: "", google_location: "" });
                    toast.success("Developer added");
                  } catch (e) { toast.error("Failed to add developer"); }
                }} className="flex-1 bg-[#0f2744] hover:bg-[#1e3a5f] text-white" data-testid="save-new-dev-btn">Save Developer</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Mandate Modal */}
      {showAddMandateModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowAddMandateModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-[90%] max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-[#0f2744] to-[#1e3a5f] p-4 rounded-t-xl">
              <h3 className="text-white font-semibold">Add New Mandate</h3>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <Label>Mandate Company Name <span className="text-red-500">*</span></Label>
                <Input value={newMandateForm.name} onChange={e => setNewMandateForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., ABC Realtors Pvt Ltd" className="mt-1" data-testid="new-mandate-name-input" />
              </div>
              <div>
                <Label>Contact Number <span className="text-red-500">*</span></Label>
                <Input value={newMandateForm.contact} onChange={e => setNewMandateForm(p => ({ ...p, contact: e.target.value }))} placeholder="+91 XXXXX XXXXX" className="mt-1" data-testid="new-mandate-contact-input" />
              </div>
              <div>
                <Label>Address</Label>
                <Input value={newMandateForm.address} onChange={e => setNewMandateForm(p => ({ ...p, address: e.target.value }))} placeholder="Mandate company address" className="mt-1" data-testid="new-mandate-address-input" />
              </div>
              <div>
                <Label>Details / Description</Label>
                <textarea value={newMandateForm.details} onChange={e => setNewMandateForm(p => ({ ...p, details: e.target.value }))} placeholder="Additional details about the mandate company" className="mt-1 w-full min-h-[80px] rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm focus:border-[#1e3a5f] focus:ring-1 focus:ring-[#1e3a5f] outline-none" data-testid="new-mandate-details-input" />
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => { setShowAddMandateModal(false); setNewMandateForm({ name: "", contact: "", address: "", details: "" }); }} className="flex-1">Cancel</Button>
                <Button onClick={async () => {
                  if (!newMandateForm.name.trim()) { toast.error("Mandate name is required"); return; }
                  if (!newMandateForm.contact.trim()) { toast.error("Contact number is required"); return; }
                  try {
                    const token = localStorage.getItem("token");
                    const res = await axios.post(`${API}/mandates`, newMandateForm, { headers: { Authorization: `Bearer ${token}` } });
                    setSmMandates(prev => [res.data, ...prev]);
                    setFormData(prev => ({ ...prev, mandate_id: res.data.id }));
                    axios.put(`${API}/users/set-context`, { active_developer_id: formData.developer_id || null, active_mandate_id: res.data.id }, { headers: { Authorization: `Bearer ${token}` } }).then(() => window.dispatchEvent(new Event('context-changed')));
                    setShowAddMandateModal(false);
                    setNewMandateForm({ name: "", contact: "", address: "", details: "" });
                    toast.success("Mandate added");
                  } catch (e) { toast.error("Failed to add mandate"); }
                }} className="flex-1 bg-[#0f2744] hover:bg-[#1e3a5f] text-white" data-testid="save-new-mandate-btn">Save Mandate</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Project Modal */}
      {showAddProjectModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowAddProjectModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-[90%] max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-[#0f2744] to-[#1e3a5f] p-4 rounded-t-xl">
              <h3 className="text-white font-semibold">Add New Project</h3>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <Label>Project Name <span className="text-red-500">*</span></Label>
                <Input value={newProjectForm.name} onChange={e => setNewProjectForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Sunrise Heights" className="mt-1" data-testid="new-project-name-input" />
              </div>
              <div>
                <Label>Project Type</Label>
                <select value={newProjectForm.project_type} onChange={e => setNewProjectForm(p => ({ ...p, project_type: e.target.value }))} className="mt-1 h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm" data-testid="new-project-type-input">
                  <option value="">Select Type</option>
                  <option value="residential">Residential</option>
                  <option value="commercial">Commercial</option>
                  <option value="mixed">Mixed Use</option>
                </select>
              </div>
              <div>
                <Label>Project Address</Label>
                <Input value={newProjectForm.address} onChange={e => setNewProjectForm(p => ({ ...p, address: e.target.value }))} placeholder="Project site address" className="mt-1" data-testid="new-project-address-input" />
              </div>
              <div>
                <Label>Google Location Link</Label>
                <Input value={newProjectForm.google_location} onChange={e => setNewProjectForm(p => ({ ...p, google_location: e.target.value }))} placeholder="https://maps.google.com/..." className="mt-1" data-testid="new-project-location-input" />
              </div>
              <div>
                <Label>Details / Description</Label>
                <textarea value={newProjectForm.details} onChange={e => setNewProjectForm(p => ({ ...p, details: e.target.value }))} placeholder="Additional project details" className="mt-1 w-full min-h-[80px] rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm focus:border-[#1e3a5f] focus:ring-1 focus:ring-[#1e3a5f] outline-none" data-testid="new-project-details-input" />
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => { setShowAddProjectModal(false); setNewProjectForm({ name: "", project_type: "", address: "", google_location: "", details: "" }); }} className="flex-1">Cancel</Button>
                <Button onClick={async () => {
                  if (!newProjectForm.name.trim()) { toast.error("Project name is required"); return; }
                  try {
                    const token = localStorage.getItem("token");
                    const res = await axios.post(`${API}/projects`, newProjectForm, { headers: { Authorization: `Bearer ${token}` } });
                    setSmProjects(prev => [res.data, ...prev]);
                    setFormData(prev => ({ ...prev, project_id: res.data.id }));
                    axios.put(`${API}/users/set-context`, { active_project_id: res.data.id }, { headers: { Authorization: `Bearer ${token}` } });
                    setShowAddProjectModal(false);
                    setNewProjectForm({ name: "", project_type: "", address: "", google_location: "", details: "" });
                    toast.success("Project added");
                  } catch (e) { toast.error("Failed to add project"); }
                }} className="flex-1 bg-[#0f2744] hover:bg-[#1e3a5f] text-white" data-testid="save-new-project-btn">Save Project</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
